/* eslint-disable @typescript-eslint/no-magic-numbers */
import moment from "moment-timezone";

import * as time from "./time";
import * as Assets from "../assets";
import { TDateTime, TDeviceAttribute } from "../types";
import { IMilestone } from "../types/deprecated/IMilestone";
import { Alarm, Segment, Shipment } from "../models";

export const getBoolean = (value?: boolean | string | number): boolean => {
	switch (value) {
		case true:
		case "true":
		case 1:
		case "1":
		case "on":
		case "On":
		case "ON":
		case "yes":
		case "Yes":
		case "YES":
			return true;
		default:
			return false;
	}
};

export const sortMilestonesByOrder = (m1: IMilestone, m2: IMilestone): number => {
	if (m1.order && m2.order) {
		return m1.order - m2.order;
	}
	const epoch1: number = m1.actualEpochTime || m1.estimatedEpochTime || m1.scheduledEpochTime || 0;
	const epoch2: number = m2.actualEpochTime || m2.estimatedEpochTime || m2.scheduledEpochTime || 0;

	return epoch1 - epoch2;
};

export const sortMilestones = (m1: IMilestone, m2: IMilestone): number => {
	const order1 = m1?.order ?? 0;
	const order2 = m2?.order ?? 0;
	const epoch1: number = m1.actualEpochTime || m1.estimatedEpochTime || m1.scheduledEpochTime || 0;
	const epoch2: number = m2.actualEpochTime || m2.estimatedEpochTime || m2.scheduledEpochTime || 0;

	return (order1 - order2) || (epoch1 - epoch2);
};

export const getDateTimeForMilestone = (milestone: IMilestone): TDateTime => {
	const epoch = milestone.actualEpochTime || milestone.estimatedEpochTime || milestone.scheduledEpochTime;
	if (!epoch) {
		return {
			time: "--:--",
			tzAbbr: "",
		};
	}

	const momentDate = moment(epoch).tz(milestone.timeZone ?? "GMT0");
	let tzAbbr;
	if (milestone.timeZone === "Asia/Dubai") {
		tzAbbr = "DT";
	}

	return {
		date: momentDate.format("D MMMM YYYY"),
		time: momentDate.format("HH:mm"),
		tzAbbr: tzAbbr || momentDate.format("z"),
	};
};

export const checkStrValue = (s?: string): string => (s ? s : "--");

export const checkTimestampValue = (n?: number, format = "MM/DD/YYYY"): string => (!!n && !isNaN(n) ? moment(n).format(format) : "--");

export const equalsArrays = <T>(a1?: T[], a2?: T[]): boolean => !!a1 && !!a2 && a1.length === a2.length && a1.sort().every((value: any, index: number) => value === a2.sort()[index]);

export const flatten = <T>(arr: T[]): T[] => arr.reduce((flat: T[], toFlatten: T | T[]) => flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten), []);

export const prettyPrint = (obj: any): string => JSON.stringify(typeof obj === "string" ? JSON.parse(obj) : obj, null, 2);

export const defaultTimelineIcons: Map<string, any> = new Map<string, any>([
	["Airport", Assets.Airport],
	["Bioporter", Assets.Bioporter],
	["Cart", Assets.Cart],
	["Checkpoint", Assets.Checkpoint],
	["CrossDock", Assets.CrossDock],
	["Customs", Assets.Customs],
	["Factory", Assets.Factory],
	["Fridge", Assets.Fridge],
	["Office", Assets.Office],
	["Retail", Assets.Retail],
	["Seaport", Assets.Seaport],
	["Warehouse", Assets.Warehouse],
	["WhiteChart", Assets.WhiteChart],
	["Hospital", Assets.Hospital],
	["Line", Assets.Line],
	["Courier", Assets.Courier],
	["Air", Assets.AirTransport],
	["Road", Assets.FrontalTruck],
	["Rail", Assets.TrainFront],
	["Sea", Assets.Ocean],
	["Mail", Assets.Email],
	["Home", Assets.Home],
	["Forklift", Assets.Forklift],
	["Freezer", Assets.Freezer],
	["Thaw", Assets.Thaw],
	["Split", Assets.Split],
	["Collect Sample", Assets.CollectSample],
]);

export const getIconSrc = (alarm: Alarm): string => {
	if (alarm.sensor.includes("Battery") || alarm.sensor.includes("BATTERY")) {
		return Assets.Sensors.Battery;
	}

	if (alarm.sensor.includes("Humidity") || alarm.sensor.includes("HUMIDITY")) {
		return Assets.Sensors.Humidity;
	}

	if (alarm.sensor.includes("TEMP_PROBE") || alarm.sensor.includes("TEMPPROBE")) {
		return Assets.Sensors.TempProbe;
	}

	if (alarm.sensor.includes("Temp") || alarm.sensor.includes("TEMPERATURE") || alarm.sensor.includes("TEMP_PROBE") || alarm.sensor.includes("TEMPPROBE")) {
		return Assets.Sensors.Temperature;
	}

	if (alarm.sensor.includes("LIGHT")) {
		return Assets.Sensors.Light;
	}

	if (alarm.sensor.includes("SHOCK")) {
		return Assets.Sensors.Shock;
	}

	if (alarm.sensor.includes("TILT")) {
		return Assets.Sensors.Orientation;
	}

	if (alarm.sensor.includes("PRESSURE")) {
		return Assets.Sensors.Pressure;
	}

	if (alarm.sensor.includes("BATTERYVOLT")) {
		return Assets.Sensors.Voltage;
	}

	return Assets.ImgBroken;
};

export const getSensorDisplayValue = (inputValue?: string | number | boolean, config?: TDeviceAttribute): string => {
	const defaultValue = "--";

	if (inputValue === undefined) {
		return defaultValue;
	}

	if (!config) {
		return inputValue.toString();
	}

	switch (config.type) {
		case "number": {
			const value = inputValue as number;
			const unit = config.unit ? " " + config.unit : "";

			if (value <= 100 && value >= -100) {
				return `${value.toFixed(1)}${unit}`;
			}

			if ((value > 100 && value < 10000) || (value < -100 && value > -10000)) {
				return `${value.toFixed(1)}${unit}`;
			}

			return `${(value / 1000).toFixed(1)}k${unit}`;
		}
		case "boolean": {
			return inputValue ? config.trueValue : config.falseValue;
		}
		default:
			return defaultValue;
	}
};

export { setupHighcharts } from "./highchartsUtils";
export { time };

export const getHistoryEndToEndInterval = (shipment: Shipment, orderId: string, legs: string[], isLiveLegExtended?: boolean): { startTime: number; endTime: number; inProgress: boolean } => {

	let startTime = 0;
	let endTime = 0;
	let segments;
	let inProgress = false;
	let isNotStarted = false;
	let isCompleted = false;
	let lastSegmentRoute: Segment | undefined;
	let firstSegmentRoute: Segment | undefined;
	if (orderId.toUpperCase() === shipment.masterTrackingNo.toUpperCase()) {
		lastSegmentRoute = shipment.segments.findLast(segment => segment.leg === legs[legs.length - 1]);
		firstSegmentRoute = shipment.segments.find(segment => segment.leg === legs[0]);
	} else {
		segments = shipment.segments.filter(segment => segment.routeIdentifier === orderId && legs.includes(segment.leg));

		if (segments.length) { // check that user can see that leg. For Kite and Hospital
			lastSegmentRoute = segments[segments.length - 1];
			[firstSegmentRoute] = segments;

		} else {
			return {
				startTime,
				endTime,
				inProgress,
			};
		}
	}
	if (!lastSegmentRoute || !firstSegmentRoute) { // bad legs in segments
		return {
			startTime,
			endTime,
			inProgress,
		};
	}

	const firstSegmentRouteIndex: number | null = firstSegmentRoute.segmentSequenceNo;
	const lastSegmentRouteIndex: number | null = lastSegmentRoute.segmentSequenceNo;
	const lastSegmentNodeIndex = lastSegmentRoute.segmentSequenceNo + 1;
	const lastSegmentIndex = isLiveLegExtended ? lastSegmentNodeIndex : lastSegmentRouteIndex;
	const firstSegmentNodeIndex = firstSegmentRouteIndex - 1 || 0;
	const startSegmentRouteIndex: number | undefined = firstSegmentNodeIndex - 1 > 0 ? (firstSegmentNodeIndex - 1) : undefined;
	const endSegmentRouteIndex: number | undefined = lastSegmentNodeIndex + 1 <= shipment.segments.length ? lastSegmentNodeIndex + 1 : undefined;
	inProgress = shipment.segments.some(segment => segment.status === "InProgress" && segment.segmentSequenceNo <= lastSegmentIndex && segment.segmentSequenceNo >= firstSegmentRouteIndex);
	isNotStarted = !!shipment.segments.length && shipment.segments.filter(segment => segment.segmentSequenceNo <= lastSegmentIndex && segment.segmentSequenceNo >= firstSegmentRouteIndex).every(segment => segment.status === "NotStarted");
	isCompleted = !!shipment.segments.length && shipment.segments.filter(segment => segment.segmentSequenceNo <= lastSegmentIndex && segment.segmentSequenceNo >= firstSegmentRouteIndex).every(segment => segment.status === "Completed");

	if (isNotStarted && firstSegmentNodeIndex === 1 && !isCompleted) { // for NotStarted shipments for Marken and Kite view
		// if the first segment status is NotStarted - it means that the first segment(=shipment) is InProgress.
		inProgress = true;
	}
	if (startSegmentRouteIndex === undefined) {
		startTime = 1;
	} else {
		// const startSegmentRoute = shipment.segments.find(segment => segment.segmentSequenceNo === startSegmentRouteIndex);
		firstSegmentRoute?.milestones.forEach(milestone => {
			const milestoneTime = milestone.actualEpochTime;
			if (milestoneTime) {
				if (!startTime) {
					startTime = milestoneTime;
				} else {
					startTime = milestoneTime < startTime ? milestoneTime : startTime;
				}
			}
		});
	}
	if (endSegmentRouteIndex === undefined) {
		endTime = Date.now();
	} else {

		if (isLiveLegExtended) {
			const endSegmentRoute = shipment.segments.find(segment => segment.segmentSequenceNo === endSegmentRouteIndex);
			endSegmentRoute?.milestones.forEach(milestone => {
				const milestoneTime = milestone.actualEpochTime;
				if (milestoneTime) {
					if (!endTime) {
						endTime = milestoneTime;
					} else {
						endTime = milestoneTime < endTime ? milestoneTime : endTime;
					}
				}
			});
		} else {
			// const endSegmentRoute = shipment.segments.find(segment => segment.segmentSequenceNo === endSegmentRouteIndex);
			lastSegmentRoute?.milestones.forEach(milestone => {
				const milestoneTime = milestone.actualEpochTime;
				if (milestoneTime) {
					if (!endTime) {
						endTime = milestoneTime;
					} else {
						endTime = milestoneTime > endTime ? milestoneTime : endTime;
					}
				}
			});
		}
	}

	if (inProgress) {
		endTime = Date.now();
	}

	return {
		startTime,
		endTime,
		inProgress,
	};
};

export const sensorIcons: Map<string, string> = new Map<string, string>([
	["battery", Assets.Sensors.Battery],
	["temperature", Assets.Sensors.Temperature],
	["humidity", Assets.Sensors.Humidity],
	["cargo", Assets.Sensors.Cargo],
	["doorOpen", Assets.Sensors.DoorOpen],
	["voltage", Assets.Sensors.Voltage],
	["signal", Assets.Sensors.Signal],
	["shock", Assets.Sensors.Shock],
	["orientation", Assets.Sensors.Orientation],
	["tempprobe", Assets.Sensors.TempProbe],
	["light", Assets.Sensors.Light],
	["irlight", Assets.Sensors.IrLight],
	["gpsjamming", Assets.Sensors.GpsJamming],
	["pressure", Assets.Sensors.Pressure],
]);
